<template>
  <div
    v-if="componentIsInitialized && model.isInitialized"
    class="timeline-calendar-container"
  >
    <c-timeline-year-switch
      v-if="showYearSwitch"
      v-model="model"
      v-bind="yearSwitchOptions"
    />

    <div class="tw-flex">
      <c-timeline-period-switch
        v-if="showPeriodSwitch && calendarType === 'period'"
        v-model="model"
        switch-direction="previous"
        switch-type="period"
      />

      <div class="timeline-calendar">
        <component
          v-for="calendarComponent in calendarComponents"
          :is="calendarComponent"
          v-model="model"
          :show-current-day-indicator
        />
      </div>

      <c-timeline-period-switch
        v-if="showPeriodSwitch && calendarType === 'period'"
        v-model="model"
        switch-direction="next"
        switch-type="period"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import CTimelineMonths from "components/wrappers/timeline-calendar/CTimelineMonths.vue";
import CTimelineWeeks from "components/wrappers/timeline-calendar/CTimelineWeeks.vue";
import CTimelineDays from "components/wrappers/timeline-calendar/CTimelineDays.vue";
import CTimelinePeriods from "components/wrappers/timeline-calendar/CTimelinePeriods.vue";
import CTimelinePeriodSwitch from "components/wrappers/timeline-calendar/CTimelinePeriodSwitch.vue";
import CTimelineYearSwitch from "components/wrappers/timeline-calendar/CTimelineYearSwitch.vue";
import TimelineCalendarModel, {
  TIMELINE_CALENDAR_DEFAULT_TYPE,
  TIMELINE_CALENDAR_TYPES,
} from "models/TimelineCalendarModel";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";

const learningObjectivesStore = useLearningObjectivesStore();

const emit = defineEmits(["update:schoolYear"]);

const selectedPeriod = defineModel("period", {
  type: [null, Object],
  required: true,
  default: () => null,
});

// Timeline calendar model
let model = {};

const props = defineProps({
  showMonths: {
    type: Boolean,
    default: true,
  },
  showWeeks: {
    type: Boolean,
    default: true,
  },
  showDays: {
    type: Boolean,
    default: true,
  },
  showPeriods: {
    type: Boolean,
    default: true,
  },
  calendarType: {
    type: String,
    default: () => TIMELINE_CALENDAR_DEFAULT_TYPE,
    validator: (value) => {
      // The value must match one of these strings
      return TIMELINE_CALENDAR_TYPES.includes(value);
    },
  },
  showPeriodSwitch: {
    type: Boolean,
    default: true,
  },
  yearSwitch: {
    type: [String, Array, Boolean],
    default: () => true,
    validator: (value) => {
      if (typeof value === "boolean") {
        return true;
      }

      // The value must match one of these values: true, false, 'future', 'past', or an array of: 'future', 'past'
      const validStringTypes = ["future", "past"];

      if (Array.isArray(value)) {
        return value.every((type) => validStringTypes.includes(type));
      }

      return validStringTypes.includes(value);
    },
  },
  showCurrentDayIndicator: {
    type: Boolean,
    default: true,
  },
});

// Year switch
const showYearSwitch = computed(() => {
  const { yearSwitch } = props;

  if (typeof yearSwitch === "boolean") {
    return yearSwitch;
  }

  // The value must match one of these values: true, false, 'future', 'past', or an array of: 'future', 'past'
  const validStringTypes = ["future", "past"];

  if (Array.isArray(yearSwitch)) {
    return (
      yearSwitch.length > 0 &&
      yearSwitch.every((type) => validStringTypes.includes(type))
    );
  }

  return validStringTypes.includes(yearSwitch);
});
const yearSwitchOptions = computed(() => {
  const { yearSwitch } = props;

  if (typeof yearSwitch === "boolean") {
    return yearSwitch ? { switchPast: true, switchFuture: true } : {};
  }

  const switchPast = Array.isArray(yearSwitch)
    ? yearSwitch.includes("past")
    : yearSwitch === "past";
  const switchFuture = Array.isArray(yearSwitch)
    ? yearSwitch.includes("future")
    : yearSwitch === "future";

  return { switchPast, switchFuture };
});

// Show components based on props
const calendarComponents = computed(() => {
  const { showDays, showWeeks, showMonths, showPeriods } = props;

  return [
    ...(showMonths ? [CTimelineMonths] : []),
    ...(showWeeks ? [CTimelineWeeks] : []),
    ...(showDays ? [CTimelineDays] : []),
    ...(showPeriods ? [CTimelinePeriods] : []),
  ];
});

const TIMELINE_COLUMN_WIDTH_IN_PIXELS = computed(() => {
  const { showDays } = props;

  switch (true) {
    case showDays:
      return "20px";
    default:
      return "30px";
  }
});

// This will be ready when the component is fully initialized
const componentIsInitialized = ref(false);

watch(
  () => [
    props.showDays,
    props.showWeeks,
    props.showMonths,
    props.showPeriods,
    props.calendarType,
    props.showCurrentDayIndicator,
  ],
  ([
    showDays,
    showWeeks,
    showMonths,
    showPeriods,
    calendarType,
    showCurrentDayIndicator,
  ]) => {
    model.updateView({
      showDays,
      showWeeks,
      showMonths,
      showPeriods,
      calendarType,
      showCurrentDayIndicator,
    });
  },
);

const selectedPeriodHandler = (newVal) => {
  selectedPeriod.value = newVal;
};

onBeforeMount(() => {
  const {
    showDays,
    showWeeks,
    showMonths,
    showPeriods,
    calendarType,
    showCurrentDayIndicator,
  } = props;

  const calendarOptions = {
    view: {
      showDays,
      showWeeks,
      showMonths,
      showPeriods,
      showCurrentDayIndicator,
    },
    calendarType,
    period: selectedPeriod.value,
  };

  model = new TimelineCalendarModel(calendarOptions, learningObjectivesStore);

  // Register the listener
  model.onSelectedPeriodChange(selectedPeriodHandler);

  componentIsInitialized.value = true;
});

onBeforeUnmount(() => {
  // Clean up the listener to avoid memory leaks
  if (model && selectedPeriodHandler) {
    model.offSelectedPeriodChange(selectedPeriodHandler);
  }
});
</script>

<style lang="scss">
.timeline-calendar-container {
  @apply tw-flex tw-flex-col;
}

.timeline-calendar {
  @apply tw-grid tw-w-full tw-overflow-auto tw-border-slate-200 tw-pb-3;
  grid-template-columns: repeat(
    v-bind("model.gridCount"),
    minmax(v-bind(TIMELINE_COLUMN_WIDTH_IN_PIXELS), 1fr)
  );

  &__row {
    @apply tw-grid tw-grid-cols-subgrid tw-border-inherit;
    grid-column: span v-bind("model.gridCount") / span v-bind("model.gridCount");
  }

  &__row {
    &:not(:last-of-type) {
      @apply tw-border-b;
    }

    &--periods {
      background: repeating-linear-gradient(
        -45deg,
        rgb(var(--color-striped-bg-light)),
        rgb(var(--color-striped-bg-light)) 5px,
        rgb(var(--color-striped-bg-dark)) 5px,
        rgb(var(--color-striped-bg-dark)) 10px
      );
    }

    .timeline-calendar__item:not(:last-of-type) {
      @apply tw-border-r;
    }
  }

  &__item {
    @apply tw-flex tw-select-none tw-items-center tw-justify-center tw-border-inherit tw-p-2;
    --grid-columns: attr(data-grid-columns type(<number>));
    grid-column: span var(--grid-columns) / span var(--grid-columns);
  }

  &__periods {
    @apply tw-min-h-[44px] tw-p-1;
  }

  &__days {
    @apply tw-text-xs;
  }

  &__weeks {
    @apply tw-text-xs;

    &.current-week {
      @apply tw-relative tw-z-10;
      background: rgba(249, 115, 22, 0.1);

      &::before {
        @apply tw-absolute tw-bottom-0 tw-h-[2px] tw-w-full tw-content-[''];
        background: rgba(249, 115, 22, 0.75);
      }
    }
  }

  &__months {
    @apply tw-py-2 tw-text-sm tw-font-medium;
  }
}
</style>
